import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  loadPlantillasCiclicos,
  STORAGE_PLANTILLAS_CICLICOS,
  lineasToBloques,
} from '../../../data/mockPlantillasCiclicos';
import {
  fetchPlaneamientoDatosPlantillasCiclicos,
  importarPlantillasCiclicasBulk,
  createPlantillaCiclica,
  updatePlantillaCiclica,
  deletePlantillaCiclica,
} from '../../../services/plantillaCiclicaService';
import { fetchCiclicosList } from '../../../services/ciclicoService';

const newBlockId = () => `blk-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const DIAS_LABEL = [
  { key: 'lunes', label: 'Lunes' },
  { key: 'martes', label: 'Martes' },
  { key: 'miercoles', label: 'Miércoles' },
  { key: 'jueves', label: 'Jueves' },
  { key: 'viernes', label: 'Viernes' },
  { key: 'sabado', label: 'Sábado' },
  { key: 'domingo', label: 'Domingo' },
];

function emptyDiasSemanaLV() {
  return {
    lunes: true,
    martes: true,
    miercoles: true,
    jueves: true,
    viernes: true,
    sabado: false,
    domingo: false,
  };
}

const PlantillasCiclicos = () => {
  const location = useLocation();
  const [plantillas, setPlantillas] = useState([]);
  const [datosLoading, setDatosLoading] = useState(true);
  const [plantillasError, setPlantillasError] = useState(null);
  const [plantillaSaving, setPlantillaSaving] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);
  const [tiemposDisponibles, setTiemposDisponibles] = useState([]);
  const [tiposPlatoDisponibles, setTiposPlatoDisponibles] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editId, setEditId] = useState(null);
  const [nombre, setNombre] = useState('');
  const [diasSemana, setDiasSemana] = useState(() => emptyDiasSemanaLV());
  /** Bloques: un tiempo + orden de tipos de plato (`ordenTipos`: IDs en orden; filas vacías con '') */
  const [bloques, setBloques] = useState([]);
  /** Tiempo elegido en el desplegable antes de pulsar "Añadir tiempo" */
  const [tiempoParaAgregar, setTiempoParaAgregar] = useState('');
  const [ciclicosApi, setCiclicosApi] = useState([]);

  const tiemposSinUsar = useMemo(
    () =>
      tiemposDisponibles.filter((t) => !bloques.some((b) => String(b.tiempoId) === String(t.id))),
    [tiemposDisponibles, bloques],
  );

  /** Cíclicos en API que referencian cada `plantillaId`. */
  const usoCiclosPorPlantilla = useMemo(() => {
    const m = new Map();
    for (const c of ciclicosApi) {
      const k = String(c.plantillaId || '');
      if (!k) continue;
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [ciclicosApi]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDatosLoading(true);
      setPlantillasError(null);
      try {
        const bundle = await fetchPlaneamientoDatosPlantillasCiclicos();
        if (cancelled) return;
        let { plantillas: list, tiempos, tiposPlato } = bundle;
        if (list.length === 0) {
          const local = loadPlantillasCiclicos();
          if (local.length > 0) {
            const payload = local.map((p) => ({
              nombre: p.nombre,
              proyectoId: p.proyectoId || null,
              diasSemana: p.diasSemana,
              bloques: p.bloques,
            }));
            try {
              list = await importarPlantillasCiclicasBulk(payload);
              try {
                localStorage.removeItem(STORAGE_PLANTILLAS_CICLICOS);
              } catch {
                /* ignore */
              }
            } catch {
              /* import inválido (p. ej. proyectos) — se mantiene lista vacía */
            }
          }
        }
        if (cancelled) return;
        setPlantillas(list);
        setTiemposDisponibles(tiempos.filter((t) => t.active));
        setTiposPlatoDisponibles(tiposPlato.filter((t) => t.active));
      } catch (e) {
        if (!cancelled) {
          setPlantillasError(
            e?.message || 'No se pudieron cargar los datos. Compruebe su sesión y el servidor.',
          );
          setPlantillas([]);
          setTiemposDisponibles([]);
          setTiposPlatoDisponibles([]);
        }
      } finally {
        if (!cancelled) setDatosLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchCiclicosList();
        if (!cancelled) setCiclicosApi(list);
      } catch {
        if (!cancelled) setCiclicosApi([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.key, plantillas.length]);

  const resetModalForm = useCallback(() => {
    setNombre('');
    setDiasSemana(emptyDiasSemanaLV());
    setBloques([]);
    setTiempoParaAgregar('');
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditId(null);
    setModalMode('create');
    resetModalForm();
  }, [resetModalForm]);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, closeModal]);

  useEffect(() => {
    if (modalOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const openCreate = () => {
    setModalMode('create');
    setEditId(null);
    resetModalForm();
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setModalMode('edit');
    setEditId(p.id);
    setNombre(p.nombre);
    setDiasSemana({ ...emptyDiasSemanaLV(), ...p.diasSemana });
    let bs = [];
    if (Array.isArray(p.bloques) && p.bloques.length > 0) {
      bs = p.bloques.map((b) => {
        const orden =
          Array.isArray(b.ordenTipos) && b.ordenTipos.length
            ? [...b.ordenTipos]
            : Array.isArray(b.tipoPlatoIds) && b.tipoPlatoIds.length
              ? [...b.tipoPlatoIds]
              : [''];
        return {
          id: b.id || newBlockId(),
          tiempoId: b.tiempoId || '',
          ordenTipos: orden,
        };
      });
    } else if (Array.isArray(p.lineas) && p.lineas.length > 0) {
      bs = lineasToBloques(p.lineas);
    }
    setBloques(bs);
    setTiempoParaAgregar('');
    setModalOpen(true);
  };

  const eliminarPlantilla = async (p) => {
    const v = usoCiclosPorPlantilla.get(String(p.id)) || 0;
    if (v > 0) {
      window.alert(
        `No se puede eliminar: hay ${v} cíclico(s) que usan esta plantilla. Cambie o elimine esos cíclicos en «Planeamiento → Cíclicos».`,
      );
      return;
    }
    if (!window.confirm(`¿Eliminar la plantilla «${p.nombre}»? Esta acción no se puede deshacer.`)) {
      return;
    }
    setEliminandoId(p.id);
    try {
      await deletePlantillaCiclica(p.id);
      setPlantillas((prev) => prev.filter((x) => String(x.id) !== String(p.id)));
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar la plantilla.');
    } finally {
      setEliminandoId(null);
    }
  };

  const toggleDia = (key) => {
    setDiasSemana((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const agregarTiempoABloques = () => {
    if (!tiempoParaAgregar) {
      window.alert('Seleccione un tiempo de comida.');
      return;
    }
    if (bloques.some((b) => String(b.tiempoId) === String(tiempoParaAgregar))) {
      window.alert('Ese tiempo ya está en la plantilla.');
      return;
    }
    setBloques((prev) => [
      ...prev,
      { id: newBlockId(), tiempoId: tiempoParaAgregar, ordenTipos: [''] },
    ]);
    setTiempoParaAgregar('');
  };

  const quitarBloque = (blockId) => {
    setBloques((prev) => prev.filter((b) => b.id !== blockId));
  };

  /** Tipos disponibles en la fila `index`: no repetir en otras filas (la fila actual puede conservar su valor). */
  const opcionesTipoParaFila = useCallback(
    (bloque, index) => {
      const orden = bloque.ordenTipos || [];
      const otrosIds = new Set(
        orden.map((id, j) => (j !== index && id ? String(id) : null)).filter(Boolean),
      );
      return tiposPlatoDisponibles.filter(
        (tp) => String(tp.id) === String(orden[index] ?? '') || !otrosIds.has(String(tp.id)),
      );
    },
    [tiposPlatoDisponibles],
  );

  const setOrdenTipoEnFila = (blockId, index, tipoId) => {
    setBloques((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const arr = [...(b.ordenTipos || [])];
        arr[index] = tipoId;
        return { ...b, ordenTipos: arr };
      }),
    );
  };

  const agregarFilaTipo = (blockId) => {
    setBloques((prev) =>
      prev.map((b) =>
        b.id === blockId ? { ...b, ordenTipos: [...(b.ordenTipos || []), ''] } : b,
      ),
    );
  };

  const quitarFilaTipo = (blockId, index) => {
    setBloques((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const arr = [...(b.ordenTipos || [])];
        arr.splice(index, 1);
        return { ...b, ordenTipos: arr.length ? arr : [''] };
      }),
    );
  };

  const moverFilaTipo = (blockId, index, delta) => {
    setBloques((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        const arr = [...(b.ordenTipos || [])];
        const j = index + delta;
        if (j < 0 || j >= arr.length) return b;
        [arr[index], arr[j]] = [arr[j], arr[index]];
        return { ...b, ordenTipos: arr };
      }),
    );
  };

  const nombreTiempo = (tiempoId) =>
    tiemposDisponibles.find((t) => String(t.id) === String(tiempoId))?.label || tiempoId;

  const submitModal = async (e) => {
    e.preventDefault();
    const n = nombre.trim();
    if (!n) {
      window.alert('Indique un nombre para la plantilla.');
      return;
    }
    const algúnDia = DIAS_LABEL.some(({ key }) => diasSemana[key]);
    if (!algúnDia) {
      window.alert('Marque al menos un día de la semana.');
      return;
    }
    const bloquesConTipos = bloques
      .map((b) => ({
        ...b,
        ordenTipos: (b.ordenTipos || []).filter(Boolean),
      }))
      .filter((b) => b.tiempoId && b.ordenTipos.length > 0);
    if (bloquesConTipos.length === 0) {
      window.alert(
        'Añada al menos un tiempo y defina los tipos de plato (en orden) para ese tiempo.',
      );
      return;
    }
    const body = {
      nombre: n,
      proyectoId: null,
      diasSemana,
      bloques: bloquesConTipos.map((b) => ({
        id: b.id,
        tiempoId: b.tiempoId,
        ordenTipos: b.ordenTipos,
      })),
    };
    setPlantillaSaving(true);
    try {
      if (modalMode === 'create') {
        const created = await createPlantillaCiclica(body);
        setPlantillas((prev) => [...prev, created]);
      } else {
        const updated = await updatePlantillaCiclica(editId, body);
        setPlantillas((prev) =>
          prev.map((x) => (String(x.id) === String(editId) ? updated : x)),
        );
      }
      closeModal();
    } catch (err) {
      window.alert(err?.message || 'No se pudo guardar la plantilla.');
    } finally {
      setPlantillaSaving(false);
    }
  };

  const hayCatalogo =
    !datosLoading && tiemposDisponibles.length > 0 && tiposPlatoDisponibles.length > 0;
  const puedeUsarPlaneamiento = hayCatalogo;

  const algúnDiaMarcado = DIAS_LABEL.some(({ key }) => diasSemana[key]);
  const bloquesConTiposOk = bloques.some(
    (b) => b.tiempoId && (b.ordenTipos || []).filter(Boolean).length > 0,
  );
  const puedeGuardar =
    nombre.trim() && algúnDiaMarcado && bloquesConTiposOk && hayCatalogo;

  const resumenDias = (d) => {
    if (!d) return '—';
    const cortos = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    const parts = DIAS_LABEL.map(({ key }, i) => (d[key] ? cortos[i] : null)).filter(Boolean);
    return parts.length ? parts.join(', ') : 'Ninguno';
  };

  const modal = modalOpen
    ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
            aria-label="Cerrar"
            onClick={closeModal}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-plantilla-ciclica-titulo"
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl border border-gray-200"
          >
            <div className="border-b border-gray-100 px-5 py-4 sticky top-0 bg-white z-10">
              <h2 id="modal-plantilla-ciclica-titulo" className="text-lg font-semibold text-gray-900">
                {modalMode === 'create' ? 'Nueva plantilla cíclica' : 'Editar plantilla cíclica'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Añada cada tiempo de comida y elija los tipos de plato en el orden deseado (listas
                desplegables; use subir/bajar para reordenar).
              </p>
            </div>
            <form onSubmit={submitModal} className="px-5 py-4 space-y-5">
              <div>
                <label htmlFor="plantilla-nombre" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la plantilla
                </label>
                <input
                  id="plantilla-nombre"
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ej. Ciclo comedor L–V"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Días de la semana en que aplica</span>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {DIAS_LABEL.map(({ key, label }) => (
                    <label key={key} className="inline-flex items-center gap-2 cursor-pointer text-sm text-gray-800">
                      <input
                        type="checkbox"
                        checked={!!diasSemana[key]}
                        onChange={() => toggleDia(key)}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-800">Tiempos y tipos de plato</p>
                <p className="text-xs text-gray-500">
                  <strong>1)</strong> Elija un tiempo y pulse <strong>Añadir tiempo</strong>.{' '}
                  <strong>2)</strong> En cada bloque, seleccione tipos de plato en orden (una fila por tipo;
                  puede añadir filas y reordenarlas).
                </p>

                {datosLoading ? (
                  <p className="text-sm text-gray-600 py-2">Cargando tiempos y tipos de plato…</p>
                ) : tiemposDisponibles.length === 0 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                    No hay tiempos activos. Configúrelos en{' '}
                    <Link to="/produccion-concesionaria/configuraciones/tiempos" className="underline font-medium">
                      Tiempos
                    </Link>
                    .
                  </p>
                ) : tiposPlatoDisponibles.length === 0 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
                    No hay tipos de plato activos. Configúrelos en{' '}
                    <Link to="/produccion-concesionaria/configuraciones/tipo-plato" className="underline font-medium">
                      Tipo de plato
                    </Link>
                    .
                  </p>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-end rounded-lg border border-dashed border-teal-200 bg-teal-50/30 p-3">
                      <div className="flex-1 min-w-[200px]">
                        <label htmlFor="select-tiempo-nuevo" className="block text-xs font-medium text-gray-600 mb-1">
                          Tiempo de comida
                        </label>
                        <select
                          id="select-tiempo-nuevo"
                          value={tiempoParaAgregar}
                          onChange={(e) => setTiempoParaAgregar(e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        >
                          <option value="">— Seleccione tiempo —</option>
                          {tiemposSinUsar.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={agregarTiempoABloques}
                        disabled={!tiempoParaAgregar || tiemposSinUsar.length === 0}
                        className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium"
                      >
                        Añadir tiempo
                      </button>
                    </div>
                    {tiemposSinUsar.length === 0 && bloques.length > 0 && (
                      <p className="text-xs text-gray-500">Ya agregó todos los tiempos disponibles.</p>
                    )}

                    <div className="space-y-3 pt-1">
                      {bloques.length === 0 && (
                        <p className="text-sm text-gray-500 italic">Aún no hay tiempos en esta plantilla.</p>
                      )}
                      {bloques.map((bloque) => (
                        <div
                          key={bloque.id}
                          className="rounded-lg border border-gray-200 bg-gray-50/80 p-4 shadow-sm"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                            <div>
                              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Tiempo</p>
                              <p className="text-base font-semibold text-gray-900">{nombreTiempo(bloque.tiempoId)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => quitarBloque(bloque.id)}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Quitar tiempo
                            </button>
                          </div>
                          <p className="text-sm font-medium text-teal-900 mb-2">
                            Tipos de plato para este tiempo (orden del ciclo)
                          </p>
                          <ul className="space-y-2">
                            {(bloque.ordenTipos || ['']).map((tipoId, idx, arr) => (
                              <li
                                key={`${bloque.id}-tipo-${idx}`}
                                className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5"
                              >
                                <span className="text-xs text-gray-500 w-6 shrink-0">{idx + 1}.</span>
                                <select
                                  value={tipoId}
                                  onChange={(e) =>
                                    setOrdenTipoEnFila(bloque.id, idx, e.target.value)
                                  }
                                  className="flex-1 min-w-[160px] rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                >
                                  <option value="">— Tipo de plato —</option>
                                  {opcionesTipoParaFila(bloque, idx).map((tp) => (
                                    <option key={tp.id} value={tp.id}>
                                      {tp.name}
                                    </option>
                                  ))}
                                </select>
                                <div className="flex items-center gap-0.5 shrink-0">
                                  <button
                                    type="button"
                                    title="Subir"
                                    disabled={idx === 0}
                                    onClick={() => moverFilaTipo(bloque.id, idx, -1)}
                                    className="px-2 py-1 text-sm rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    ↑
                                  </button>
                                  <button
                                    type="button"
                                    title="Bajar"
                                    disabled={idx === arr.length - 1}
                                    onClick={() => moverFilaTipo(bloque.id, idx, 1)}
                                    className="px-2 py-1 text-sm rounded border border-gray-200 bg-gray-50 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    ↓
                                  </button>
                                  <button
                                    type="button"
                                    title="Quitar fila"
                                    onClick={() => quitarFilaTipo(bloque.id, idx)}
                                    className="px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded border border-transparent"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                          <button
                            type="button"
                            onClick={() => agregarFilaTipo(bloque.id)}
                            className="mt-2 text-sm text-teal-700 hover:text-teal-900 font-medium"
                          >
                            + Agregar tipo de plato
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!puedeGuardar || plantillaSaving}
                  className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {plantillaSaving
                    ? 'Guardando…'
                    : modalMode === 'create'
                      ? 'Crear plantilla'
                      : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {modal}

      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-600">Planeamiento</span>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Plantillas cíclicos</span>
      </nav>

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Plantillas cíclicos</h1>

      {!hayCatalogo && (
        <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
          Para armar plantillas necesita{' '}
          <Link to="/produccion-concesionaria/configuraciones/tiempos" className="underline font-medium">
            tiempos de comida
          </Link>{' '}
          y{' '}
          <Link to="/produccion-concesionaria/configuraciones/tipo-plato" className="underline font-medium">
            tipos de plato
          </Link>{' '}
          activos.
        </div>
      )}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="text-sm font-medium text-gray-700">Plantillas guardadas</span>
            <p className="text-xs text-gray-500 mt-0.5">Tabla: nombre, días y acciones.</p>
          </div>
          <button
            type="button"
            onClick={openCreate}
            disabled={!puedeUsarPlaneamiento}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
          >
            + Nueva plantilla
          </button>
        </div>
        {datosLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Cargando plantillas…</div>
        ) : plantillasError ? (
          <div className="p-6 text-sm text-red-800 bg-red-50 border-t border-red-100">{plantillasError}</div>
        ) : plantillas.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            {puedeUsarPlaneamiento ? (
              <>
                No hay plantillas aún. Pulse <strong className="text-gray-700">Nueva plantilla</strong> para comenzar.
              </>
            ) : (
              'Configure tiempos y tipos de plato para poder crear plantillas.'
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm divide-y divide-gray-100">
              <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Días</th>
                  <th className="px-4 py-3 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {plantillas.map((p) => {
                  const nCic = usoCiclosPorPlantilla.get(String(p.id)) || 0;
                  const bloqueada = nCic > 0;
                  return (
                    <tr key={p.id} className="hover:bg-gray-50/80 align-top">
                      <td className="px-4 py-3 font-semibold text-gray-900">{p.nombre}</td>
                      <td className="px-4 py-3 text-teal-800 font-medium">{resumenDias(p.diasSemana)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(p)}
                            className="text-sm text-teal-700 hover:text-teal-900 font-medium border border-teal-200 rounded-md px-3 py-1.5 hover:bg-teal-50"
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => eliminarPlantilla(p)}
                            disabled={bloqueada || eliminandoId === p.id || datosLoading}
                            title={
                              bloqueada
                                ? `Hay ${nCic} cíclico(s) vinculados; no se puede eliminar`
                                : 'Eliminar plantilla'
                            }
                            className="text-sm text-red-700 hover:text-red-900 font-medium border border-red-200 rounded-md px-3 py-1.5 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          >
                            {eliminandoId === p.id ? 'Borrando…' : 'Borrar'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6">
        <Link to="/produccion-concesionaria" className="text-teal-700 text-sm font-medium hover:underline">
          ← Producción concesionaria
        </Link>
      </div>
    </div>
  );
};

export default PlantillasCiclicos;
